import { useCallback, useEffect, useRef, useState } from 'react';
import api, { TOKEN_STORAGE_KEY } from '../api/client';
import { previewEmergencyClearance, clearConfirmedEmergency } from '../api/emergencyActions';

const message = error => typeof error?.response?.data?.message === 'string' ? error.response.data.message
  : error?.message || 'The emergency confirmation could not be verified.';

export default function useEmergencyClearance(user) {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  const owner = user?.id && token ? JSON.stringify([user.id, token]) : null;
  const ownerRef = useRef(owner); ownerRef.current = owner;
  const epoch = useRef(0);
  const [state, setState] = useState({ owner: null, confirmation: null, loading: false, error: '' });
  const stateRef = useRef(state); stateRef.current = state;
  useEffect(() => () => { epoch.current++; }, [owner]);
  const prepare = useCallback(async (scope, rangerId) => {
    const sequence = ++epoch.current;
    if (!owner || ownerRef.current !== owner || localStorage.getItem(TOKEN_STORAGE_KEY) !== token) return false;
    setState({ owner, confirmation: null, loading: true, error: '' });
    try {
      const confirmation = await previewEmergencyClearance(api, scope, rangerId, token, user.id);
      if (epoch.current !== sequence || ownerRef.current !== owner || localStorage.getItem(TOKEN_STORAGE_KEY) !== token) return false;
      setState({ owner, confirmation, loading: false, error: '' }); return confirmation;
    } catch (error) {
      if (epoch.current === sequence && ownerRef.current === owner && localStorage.getItem(TOKEN_STORAGE_KEY) === token)
        setState({ owner, confirmation: null, loading: false, error: message(error) });
      return false;
    }
  }, [owner, token, user?.id]);
  const submit = useCallback(async options => {
    const current = stateRef.current, sequence = epoch.current;
    if (current.owner !== owner || !current.confirmation || ownerRef.current !== owner || localStorage.getItem(TOKEN_STORAGE_KEY) !== token)
      throw new Error('Review the current emergency alerts before confirming.');
    try {
      const receipt = await clearConfirmedEmergency(api, current.confirmation, options);
      if (sequence !== epoch.current || ownerRef.current !== owner || localStorage.getItem(TOKEN_STORAGE_KEY) !== token) return false;
      setState({ owner, confirmation: null, loading: false, error: '' }); return receipt;
    } catch (error) {
      if (sequence === epoch.current && ownerRef.current === owner && localStorage.getItem(TOKEN_STORAGE_KEY) === token)
        setState(previous => ({ ...previous, error: message(error) }));
      throw error;
    }
  }, [owner, token]);
  const visible = state.owner === owner ? state : { confirmation: null, loading: false, error: '' };
  return { ...visible, prepare, submit };
}
